import { createSlice } from "@reduxjs/toolkit";
import { API_URL } from "config";
import axios from "axios";
import { dispatch } from "../index";

// ──────────────────────────── Initial State ────────────────────────────

const initialState = {
  // Event / Year selection
  eventsData: [],
  yearOptions: [],
  selectedYear: "",

  // Registration
  registerStatus: {
    registerMessage: "Registration",
    isRegisterUser: false,
    isregister: false,
    isstatusActive: false,
  },

  // Team details
  headerTeamDetails: null,

  // Event status (active/inactive badge)
  qicEventStatus: false,

  // Reload trigger for registration flow
  afterRegister: false,

  // Loading flags
  loading: {
    events: false,
    qicStatus: false,
  },

  // Error
  error: null,
};

// ────────────────────────────── Slice ──────────────────────────────────

const qicSlice = createSlice({
  name: "qic",
  initialState,
  reducers: {
    // ── Events / Year ──
    setEventsLoading(state, action) {
      state.loading.events = action.payload;
    },
    setEventsData(state, action) {
      state.eventsData = action.payload.events;
      state.yearOptions = action.payload.yearOptions;
    },
    setSelectedYear(state, action) {
      state.selectedYear = action.payload;
    },

    // ── Registration status ──
    setRegisterStatus(state, action) {
      state.registerStatus = action.payload;
    },

    // ── Team details ──
    setHeaderTeamDetails(state, action) {
      state.headerTeamDetails = action.payload;
    },

    // ── Event active badge ──
    setQicEventStatus(state, action) {
      state.qicEventStatus = action.payload;
    },

    // ── After-register reload trigger ──
    setAfterRegister(state, action) {
      state.afterRegister = action.payload;
    },

    // ── Loading ──
    setQicStatusLoading(state, action) {
      state.loading.qicStatus = action.payload;
    },

    // ── Error ──
    setError(state, action) {
      state.error = action.payload;
    },

    // ── Reset (useful when unmounting) ──
    resetQicState() {
      return initialState;
    },
  },
});

// ──────────────────────────── Async Actions ────────────────────────────

/**
 * Fetch all events, extract unique sorted years, store in Redux.
 */
export async function fetchEvents() {
  dispatch(qicSlice.actions.setEventsLoading(true));
  try {
    const response = await axios.get(`${API_URL}/api/events`);
    const events = response?.data?.events ?? [];
    const uniqueYears = Array.from(
      new Set(
        events
          .map((e) => e?.event_year ?? e?.eventYear ?? null)
          .filter((v) => v !== null && v !== undefined)
      )
    )
      .map(String)
      .sort((a, b) => Number(b) - Number(a));

    dispatch(
      qicSlice.actions.setEventsData({ events, yearOptions: uniqueYears })
    );
  } catch (error) {
    console.error("Failed to fetch event years:", error);
    dispatch(qicSlice.actions.setError(error));
  } finally {
    dispatch(qicSlice.actions.setEventsLoading(false));
  }
}

/**
 * Fetch stage dates for a given event year.
 * Called from component (combines with JWT-based registration check).
 */
export async function fetchStageDates(eventYear) {
  try {
    const response = await axios.get(`${API_URL}/api/qic/currenteventstartdate`, {
      params: { event_year: eventYear },
    });
    return response?.data?.stages?.[0] ?? null;
  } catch (error) {
    dispatch(qicSlice.actions.setError(error));
    return null;
  }
}

// ──────────────────────────── Selectors ────────────────────────────────

export const selectQicEventsData = (state) => state.qic.eventsData;
export const selectQicYearOptions = (state) => state.qic.yearOptions;
export const selectQicSelectedYear = (state) => state.qic.selectedYear;
export const selectQicRegisterStatus = (state) => state.qic.registerStatus;
export const selectQicHeaderTeamDetails = (state) => state.qic.headerTeamDetails;
export const selectQicEventStatus = (state) => state.qic.qicEventStatus;
export const selectQicAfterRegister = (state) => state.qic.afterRegister;
export const selectQicLoading = (state) => state.qic.loading;
export const selectQicError = (state) => state.qic.error;

// ──────────────────────────── Exports ──────────────────────────────────

export const {
  setEventsLoading,
  setEventsData,
  setSelectedYear,
  setRegisterStatus,
  setHeaderTeamDetails,
  setQicEventStatus,
  setAfterRegister,
  setQicStatusLoading,
  setError,
  resetQicState,
} = qicSlice.actions;

export default qicSlice.reducer;
